<script setup lang="ts">
import type { CheckboxRootEmits, CheckboxRootProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import { Check, Minus } from 'lucide';
import { CheckboxIndicator, CheckboxRoot, useForwardPropsEmits } from 'reka-ui';
import { useFieldControl } from '@/components/base/_internal/control';
import SphIcon from '@/components/base/icon';
import { cn } from '@/utils/shadcn/lib/utils.ts';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<CheckboxRootProps & {
    class?: HTMLAttributes['class']
    invalid?: boolean
}>();
const emits = defineEmits<CheckboxRootEmits>();

const delegatedProps = reactiveOmit(props, 'class', 'invalid');

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const field = useFieldControl(() => props, () => 'auto');
</script>

<template>
    <CheckboxRoot
        v-slot="slotProps"
        data-slot="checkbox"
        v-bind="forwarded"
        :id="field.id"
        :disabled="field.disabled"
        :required="field.required"
        :aria-describedby="field.describedBy"
        :aria-invalid="field.invalid ? 'true' : 'false'"
        class="sph-form-control sph-checkbox"
        :class="cn('bg-input peer border-input-dark data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground data-[state=indeterminate]:border-primary size-6! shrink-0 rounded-sm border shadow-xs transition-shadow transition-colors outline-none',
                   props.class)"
    >
        <CheckboxIndicator
            data-slot="checkbox-indicator"
            class="grid place-content-center text-current transition-none"
            :force-mount="true"
        >
            <slot v-bind="slotProps">
                <SphIcon
                    :icon="slotProps.state === 'indeterminate' ? Minus : Check"
                    size="sm"
                />
            </slot>
        </CheckboxIndicator>
    </CheckboxRoot>
</template>

<style scoped>
.sph-checkbox :deep(svg) {
    --sph-icon-stroke-width: 2;

    stroke-dasharray: 23;
    stroke-dashoffset: 23;
    stroke: #fff;
    opacity: 0;
    transform: translate(0%, -20%);
    transition:
        stroke-dashoffset 0s,
        transform 0s,
        opacity 0.25s;
}

.sph-checkbox :deep([data-state='checked'] svg) {
    stroke-dashoffset: 46;
    opacity: 1;
    transform: translate(0, 0);
    transition:
        stroke-dashoffset 0.2s 0.1s var(--ease-out-sine),
        transform 0.4s var(--ease-out-cubic),
        opacity 0s;
}

/* The dash draw above traces the tick's two strokes; the indeterminate bar is one straight line
   and has nothing to trace, so it just appears and grows out from its centre. */
.sph-checkbox :deep([data-state='indeterminate'] svg) {
    stroke-dasharray: none;
    stroke-dashoffset: 0;
    opacity: 1;
    transform: translate(0, 0);
    transition:
        transform 0.2s var(--ease-out-cubic),
        opacity 0.15s;
}
</style>
